import { createUseStyles } from 'react-jss'
import { THEME } from 'components/theme'

// iOS/Android profile avatar with the green pencil badge, plus the Delete / Choose photo sheet
export const ProfilePhotoAvatarStyles = createUseStyles({
  block: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px 20px 16px'
  },
  button: {
    position: 'relative',
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    backgroundColor: THEME.brandLightGreen,
    color: THEME.brandGreen,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'transform 160ms ease, box-shadow 160ms ease',
    '&:hover:not(:disabled)': {
      transform: 'translateY(-1px)'
    },
    '&:focus-visible': {
      outline: `2px solid ${THEME.brandGreen}`,
      outlineOffset: 2
    },
    '&:disabled': {
      cursor: 'default'
    }
  },
  image: {
    borderRadius: '50%',
    objectFit: 'cover'
  },
  // Large profile avatar: white ring + soft shadow so it lifts off the header band
  ringed: {
    boxShadow: `0 0 0 3px ${THEME.brandWhite}, 0 6px 18px rgba(0, 0, 0, 0.18)`
  },
  // Green pencil disc on the avatar's bottom-right (iOS Profile)
  editBadge: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    backgroundColor: THEME.brandGreen,
    border: `2px solid ${THEME.brandWhite}`,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fill: THEME.onBrand
    }
  },
  uploadingOverlay: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '@keyframes profileAvatarSpin': {
    to: { transform: 'rotate(360deg)' }
  },
  uploadingSpinner: {
    borderRadius: '50%',
    border: '3px solid rgba(255, 255, 255, 0.35)',
    borderTopColor: '#fff',
    animation: '$profileAvatarSpin 0.8s linear infinite'
  },
  // Action sheet (iOS UploadImageScreen: Delete · Camera · Gallery): round icon buttons with
  // labels, nothing else — compact
  // The sheet card itself: tight, like iOS (no header, one row of actions)
  compactSheet: {
    padding: '22px 20px 20px !important',
    gap: '0 !important'
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 56,
    padding: '4px 0 0'
  },
  action: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    color: THEME.brandBlack,
    transition: 'background-color 120ms ease',
    '&:hover': {
      backgroundColor: THEME.brandGrey
    },
    '&:focus-visible': {
      outline: `2px solid ${THEME.brandGreen}`,
      outlineOffset: 2
    }
  },
  // Enlarged photo (tap on the avatar): KMP MediaViewerUi — the photo on a black ground in both
  // themes, a transparent top strip carrying only the white back arrow
  viewerScrim: {
    position: 'fixed',
    inset: 0,
    zIndex: 100002,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box'
  },
  viewerCard: {
    position: 'relative',
    backgroundColor: '#000',
    borderRadius: 16,
    // The 56px top strip is the app bar; the photo sits under it
    padding: '56px 0 0',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    maxWidth: 'calc(100vw - 48px)'
  },
  // Stage for the large photo: the blurred thumbnail and the spinner sit over it until it loads
  viewerStage: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#000',
    // Before the photo decodes the <img> has no height; the stage keeps a box so the
    // placeholder and spinner have somewhere to show
    minWidth: 240,
    minHeight: 240
  },
  // Anchored (drawer-filling) viewer: the stage takes the whole card while loading
  viewerStageAnchored: {
    width: '100%',
    flex: 1,
    minHeight: 0
  },
  viewerPlaceholder: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'blur(12px)',
    transform: 'scale(1.1)',
    opacity: 0.6
  },
  viewerSpinner: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1
  },
  // Keeps its box (min size) so the card does not jump, but stays invisible until decoded
  viewerImagePending: {
    opacity: 0
  },
  // Zoom viewport around the photo: shrink-wraps it in the card, fills the stage in a drawer
  viewerZoom: {
    display: 'inline-block'
  },
  viewerZoomAnchored: {
    display: 'block',
    width: '100%',
    height: '100%'
  },
  viewerImage: {
    display: 'block',
    objectFit: 'contain',
    minWidth: 240,
    minHeight: 240,
    maxWidth: 'min(640px, calc(100vw - 48px))',
    maxHeight: 'calc(100vh - 104px)',
    backgroundColor: '#000'
  },
  // Back arrow in the top strip (KMP: navigationIcon, iconOnDark). Above the stage (position:
  // relative, rendered after this button) and its spinner (z 1), otherwise the stage paints
  // over the button and swallows its clicks
  viewerClose: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 2,
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)'
    },
    '& svg': {
      width: 24,
      height: 24,
      fill: '#fff'
    },
    '&:focus-visible': {
      outline: '2px solid #fff',
      outlineOffset: 2
    }
  },
  // Opened from inside a drawer / panel: cover exactly that panel instead of the page.
  // Declared after the base viewer rules so these win.
  viewerScrimAnchored: {
    inset: 'auto',
    padding: 0,
    backgroundColor: 'transparent'
  },
  viewerCardAnchored: {
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    borderRadius: 0,
    justifyContent: 'center',
    overflowY: 'auto'
  },
  // Fills the stage; object-fit keeps the photo centred on the black ground either way
  viewerImageAnchored: {
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%'
  },
  actionDisc: {
    width: 60,
    height: 60,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    '& svg': {
      width: 24,
      height: 24
    }
  },
  actionDiscPrimary: {
    backgroundColor: THEME.brandGreen,
    '& svg': {
      fill: THEME.onBrand
    }
  },
  // iOS: Delete sits on a plain pale disc (not a red tint) with the bin in red
  actionDiscDanger: {
    backgroundColor: THEME.brandLightGrey,
    boxShadow: 'none',
    '& svg': {
      fill: THEME.brandRed
    }
  }
})
